import React, { useEffect, useMemo, useRef } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { EmptyItem, FormRef, GroupItem, Label, SimpleItem } from "devextreme-react/form";
import ILangue from '../../models/ILangue';
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useApi from '../../hooks/useApi';
import useLookUpBase from '../../components/selectbox/LookupBase';
import ConfigurationProvinces from './configuration-provinces';
import GestionLangues from '../gestion-langues/gestion-langues';
import ConfigurationClientsTitres from './configuration-clients-titres';
import GestionClient from '../gestion-client/gestionclient';

interface IConfigurationApplicationProps {
  isModeSelection?: boolean,
}

export default function ConfigurationApplication({ isModeSelection }: IConfigurationApplicationProps) {
  const applicationFormDataSource = useCustomDataSource<any, any>({ path: "langue", primaryKey: "idLangue" });
  const { setMainForm, selectedDataRow: selectedApplication, setIsFieldModifier, mode, setMode, setSelectedDataRow, isFieldModifier, onFieldDataChanged } = applicationFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { editorOptions: provinceParDefautEditorOption, PopupGestion: PopupGestionProvinceParDefaut } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'province', showClear: mode !== Mode.Lecture, showDropDown: isFieldModifier });
  const { editorOptions: langueParDefautEditorOption, PopupGestion: PopupGestionLangueParDefaut } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'langue', showClear: mode !== Mode.Lecture, showDropDown: isFieldModifier });
  const { editorOptions: clientTitreEditorOption, PopupGestion: PopupGestionClientTitre } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'clienttitre', showClear: mode !== Mode.Lecture, showDropDown: isFieldModifier });
  const { editorOptions: clientEditorOption, PopupGestion: PopupGestionClient } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'client', showClear: mode !== Mode.Lecture, showDropDown: isFieldModifier });

  const {
    getData: getConfig,
  } = useApi('Configuration');

  useEffect(() => {
    try {
      const names = 'DEFAULT_ID_PROVINCE,COULEUR_FOCUS,DEFAULT_ID_LANGUE,COULEUR_MODIFICATION,SAUVEGARDE_AUTOMATIQUE,UTILISER_SUFFIXES,RECHERCHE_INSTANTANEE,DEFAULT_ID_CLIENT_TITRE,SOURCE_DATE_DELAI,ID_CLIENT_ENTREPRISE,FORCER_SAISIE_DATE_NAISSANCE_CLIENT,FORCER_SAISIE_TELEPHONE_CLIENT,FORCER_SAISIE_COURRIEL_CLIENT'
      getConfig({ customPath: `Configuration/${names}/Config` })
        .then((result) => {
          setSelectedDataRow((prev: any) => {
            if (!result || typeof result !== "object")
              return prev;
            return { ...prev, ...result }
          })
        })
        .catch((err) => console.log(err))
    } catch (error) {
    }
  }, [])

  const delaiRechercheEditorOptions = useMemo(() => {
    return {
      width: '20%'
    }
  }, []);

  const couleurEditorOptions = useMemo(() => {
    return {
      width: '40%'
    }
  }, []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])


  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: any) => {
      return { ...prev, [e]: item.id }
    })
  }

  return (
    <FormListBase data={applicationFormDataSource}>
      <FormListBaseGrid dataSource={applicationFormDataSource.dataSource} onFocusedRowChanged={() => {
        if (mode === Mode.Modifier) setMode(Mode.Lecture);
      }}>
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar removeBlocked={true} headerText={`${selectedApplication?.description || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} effacerOptions={{ visible: false, disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          <TabItem title="Informations">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedApplication || {} as ILangue} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <PopupGestionProvinceParDefaut onSelectionchanged={(i) => onSelectionchanged(i, 'idcategorieclient')}>
                <ConfigurationProvinces isModeSelection={true} />
              </PopupGestionProvinceParDefaut>
              <PopupGestionLangueParDefaut onSelectionchanged={(i) => onSelectionchanged(i, 'idcategorieclient')}>
                <GestionLangues isModeSelection={true} />
              </PopupGestionLangueParDefaut>
              <PopupGestionClientTitre onSelectionchanged={(i) => onSelectionchanged(i, 'idcategorieclient')}>
                <ConfigurationClientsTitres isModeSelection={true} />
              </PopupGestionClientTitre>
              <PopupGestionClient onSelectionchanged={(i) => onSelectionchanged(i, 'idclient')}>
                <GestionClient isModeSelection={true} />
              </PopupGestionClient>
              <GroupItem caption='Paramètres généraux' colCount={2}>
                <SimpleItem key={"frm-DEFAULT_ID_PROVINCE"} dataField={"DEFAULT_ID_PROVINCE"} editorOptions={provinceParDefautEditorOption}>
                  <Label text='Province par défaut'></Label>
                </SimpleItem>
                <SimpleItem key={"frm-COULEUR_FOCUS"} dataField={"COULEUR_FOCUS"} editorType='dxColorBox' editorOptions={couleurEditorOptions}>
                  <Label text='Couleur de focus'></Label>
                </SimpleItem>
              </GroupItem>
              <GroupItem colCount={2}>
                <SimpleItem key={"frm-DEFAULT_ID_LANGUE"} dataField={"DEFAULT_ID_LANGUE"} editorOptions={langueParDefautEditorOption}>
                  <Label text='Langue par défaut'></Label>
                </SimpleItem>
                <SimpleItem key={"frm-COULEUR_MODIFICATION"} dataField={"COULEUR_MODIFICATION"} editorType='dxColorBox' editorOptions={couleurEditorOptions}>
                  <Label text='Couleur de modification'></Label>
                </SimpleItem>
              </GroupItem>
              <GroupItem colCount={2} alignItemLabels={false}>
                <SimpleItem key={"frm-SAUVEGARDE_AUTOMATIQUE"} dataField={"SAUVEGARDE_AUTOMATIQUE"} editorType='dxCheckBox'>
                  <Label text='Sauvegarde automatique'></Label>
                </SimpleItem>
                <SimpleItem key={"frm-UTILISER_SUFFIXES"} dataField={"UTILISER_SUFFIXES"} editorType='dxCheckBox'>
                  <Label text='Utiliser les suffixes (no. facturation, réservation, etc...)'></Label>
                </SimpleItem>
              </GroupItem>
              <SimpleItem key={"frm-RECHERCHE_INSTANTANEE"} dataField={"RECHERCHE_INSTANTANEE"} editorType='dxCheckBox'>
                <Label text='Recherche instantanée'></Label>
              </SimpleItem>
              <GroupItem caption='Paramètres clients'>
                <GroupItem caption='Paramètres clients' colCount={2}>
                  <SimpleItem key={"frm-DEFAULT_ID_CLIENT_TITRE"} dataField={"DEFAULT_ID_CLIENT_TITRE"} editorOptions={clientTitreEditorOption}>
                    <Label text='Titre client par défaut'></Label>
                  </SimpleItem>
                  <SimpleItem key={"frm-SOURCE_DATE_DELAI"} dataField={"SOURCE_DATE_DELAI"} editorOptions={delaiRechercheEditorOptions}>
                    <Label text='Délai recherche 411 (ms)'></Label>
                  </SimpleItem>
                </GroupItem>
                <GroupItem colCount={2}>
                  <SimpleItem key={"frm-ID_CLIENT_ENTREPRISE"} dataField={"ID_CLIENT_ENTREPRISE"} editorOptions={clientEditorOption}>
                    <Label text='Client entreprise'></Label>
                  </SimpleItem>
                  <EmptyItem></EmptyItem>
                </GroupItem>
                <SimpleItem key={"frm-FORCER_SAISIE_DATE_NAISSANCE_CLIENT"} dataField={"FORCER_SAISIE_DATE_NAISSANCE_CLIENT"} editorType='dxCheckBox'>
                  <Label text='Forcer la saisie de la date de naissance'></Label>
                </SimpleItem>
                <SimpleItem key={"frm-FORCER_SAISIE_TELEPHONE_CLIENT"} dataField={"FORCER_SAISIE_TELEPHONE_CLIENT"} editorType='dxCheckBox'>
                  <Label text='Forcer la saisie du numéro de téléphone'></Label>
                </SimpleItem>
                <SimpleItem key={"frm-FORCER_SAISIE_COURRIEL_CLIENT"} dataField={"FORCER_SAISIE_COURRIEL_CLIENT"} editorType='dxCheckBox'>
                  <Label text='Forcer la saisie du courriel'></Label>
                </SimpleItem>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedApplication || {} as ILangue} /></TabItem>
        </FormListBaseTab> : null}
    </FormListBase>
  );
}